import { useState } from "react";
import { CalendarDays, ChevronDown } from "lucide-react";

const PRESETS = [
  { key: "7d", label: "7 days", days: 7 },
  { key: "30d", label: "30 days", days: 30 },
  { key: "90d", label: "90 days", days: 90 },
  { key: "month", label: "This month" },
];

const fmt = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};
const fmtY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

// "Sep 5 – 11" · "Aug 13 – Sep 11" · "Dec 20, 2025 – Jan 3, 2026"
export function windowLabel(win) {
  if (!win) return "";
  const { from, to } = win;
  if (!from || !to) return "";
  if (from === to) return fmtY(from);
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  const sameMonth = sameYear && from.slice(0, 7) === to.slice(0, 7);
  if (sameMonth) return `${fmt(from)} – ${to.split("-")[2].replace(/^0/, "")}`;
  return sameYear ? `${fmt(from)} – ${fmt(to)}` : `${fmtY(from)} – ${fmtY(to)}`;
}

export function windowTitle(win) {
  if (!win) return "";
  const p = PRESETS.find((x) => x.key === win.preset);
  return p && win.ends_today ? `Last ${p.label.toLowerCase()}`.replace("Last this month", "This month") : windowLabel(win);
}

const todayIso = () => new Date().toLocaleDateString("sv-SE");
const shift = (days) => { const d = new Date(); d.setDate(d.getDate() - days); return d.toLocaleDateString("sv-SE"); };

// One range for the whole Overview — presets + a custom from/to. Emits {from, to} (ISO dates) or {days}.
export function RangePicker({ window: win, onChange }) {
  const [custom, setCustom] = useState(false);
  const [from, setFrom] = useState(win?.from || shift(29));
  const [to, setTo] = useState(win?.to || todayIso());
  const active = win?.preset || "30d";

  const pick = (p) => {
    setCustom(false);
    if (p.key === "month") {
      const t = todayIso();
      onChange({ from: `${t.slice(0, 7)}-01`, to: t });
    } else {
      onChange({ days: p.days });
    }
  };
  const apply = () => {
    if (!from || !to) return;
    onChange(from <= to ? { from, to } : { from: to, to: from });
    setCustom(false);
  };

  return (
    <div className="flex flex-col items-end gap-2" data-testid="overview-range">
      <div className="inline-flex items-center rounded-full border border-[#E5E0D8] bg-white p-1 shadow-sm">
        {PRESETS.map((p) => (
          <button key={p.key} data-testid={`range-${p.key}`} onClick={() => pick(p)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${active === p.key && !custom ? "bg-[#1C1917] text-white" : "text-[#57534E] hover:bg-sand-100"}`}>
            {p.label}
          </button>
        ))}
        <button data-testid="range-custom" onClick={() => setCustom((c) => !c)}
          className={`rounded-full px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${active === "custom" || custom ? "bg-[#1C1917] text-white" : "text-[#57534E] hover:bg-sand-100"}`}>
          <CalendarDays className="h-3.5 w-3.5" /> {active === "custom" && !custom ? windowLabel(win) : "Custom"} <ChevronDown className={`h-3 w-3 transition-transform ${custom ? "rotate-180" : ""}`} />
        </button>
      </div>
      {custom && (
        <div data-testid="range-custom-panel" className="flex items-center gap-2 rounded-2xl border border-[#E5E0D8] bg-white px-3 py-2 shadow-md">
          <label className="text-[11px] font-semibold text-[#8A8378]">From</label>
          <input data-testid="range-from" type="date" value={from} max={todayIso()} onChange={(e) => setFrom(e.target.value)}
            className="rounded-lg border border-sand-200 px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
          <label className="text-[11px] font-semibold text-[#8A8378]">To</label>
          <input data-testid="range-to" type="date" value={to} max={todayIso()} onChange={(e) => setTo(e.target.value)}
            className="rounded-lg border border-sand-200 px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
          <button data-testid="range-apply" onClick={apply}
            className="rounded-full bg-redline-500 text-white text-xs font-bold px-3.5 py-1.5 hover:bg-redline-600 transition-colors">Apply</button>
        </div>
      )}
    </div>
  );
}
